import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { GiftCardImageTemplate, Order, UserProfile } from '../../core/models';
import { OrderService } from '../../core/services/order.service';
import { ToastService } from '../../core/services/toast.service';
import { ValidationService } from '../../core/services/validation.service';
import { DepositModalComponent } from '../../shared/components';
import { EXT_NAME_REGEXP, GiftShareValidators } from '../../shared';
import { StaticDataActions, AuthActions } from '../../state';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { roundNumber, voucherImage } from '../../core/helpers';
import { panelInOutAnimation } from '@core/animations';
import { ViewportScroller } from '@angular/common';

export const PREVIOUS_RECIPIENT_VALUES_KEY: string = 'cart-recipient';

SwiperCore.use([Navigation, Pagination, Autoplay]);

@Component({
    selector: 'gs-cart',
    templateUrl: './cart.component.html',
    animations: [
        panelInOutAnimation
    ],
    standalone: false
})
export class CartComponent implements OnInit, OnDestroy {
  @ViewChild('confirmationModalTemplate') private confirmationModalTemplate: TemplateRef<any>;

  error: string;
  currentDate: Date = new Date();
  templates: GiftCardImageTemplate[];
  totalAmount: any = 0;
  cartItems: any = [];
  minimumOrder: any = 0;
  order: any;

  form: FormGroup = this.formBuilder.group({
    isOrderForMe: [true, [Validators.required]],
    senderName: [{ value: null, disabled: true }, [Validators.required, Validators.maxLength(100), Validators.pattern(EXT_NAME_REGEXP)]],
    recipientName: [{ value: null, disabled: true }, [Validators.required, Validators.maxLength(100), Validators.pattern(EXT_NAME_REGEXP)]],
    recipientEmail: [{ value: null, disabled: true }, [Validators.required, Validators.maxLength(256), GiftShareValidators.email]],
    recipientMessage: [{ value: null, disabled: true }, [Validators.maxLength(500)]],
    hasNotificationDate: [false],
    imageTemplateId: [1, [Validators.required]],
    notificationDate: [{ value: null, disabled: true }, [Validators.required]],
    notificationTime: [{ value: null, disabled: true }, [Validators.required]],
    hasAcceptedTerms: [false, [Validators.requiredTrue]],
  }, { validators: GiftShareValidators.futureDate('notificationDate', 'notificationTime') });

  get hasRequiredFunds(): boolean {
    return this.user && this.user.walletAmount >= this.totalAmount;
  }

  get hasMinimumOrder(): boolean {
    return this.minimumOrder <= this.totalAmount;
  }

  get controls() {
    return this.form.controls;
  }

  get hasOnlyGiftShareCards() {
    return this.cartItems.every(x => x.partnerId === environment.giftSharePartnerId);
  }

  get canScheduleNotification() {
    return !this.isOrderForMe && this.hasOnlyGiftShareCards;
  }

  get hasNotificationDate(): boolean {
    return this.form.controls.hasNotificationDate.value;
  }

  get notificationDate(): Date {
      return new Date(this.form.value.notificationDate.getFullYear(), this.form.value.notificationDate.getMonth(), this.form.value.notificationDate.getDate(),
        this.form.value.notificationTime.split(':')[0], this.form.value.notificationTime.split(':')[1])
  }

  get isOrderForMe(): boolean {
    return this.form.controls.isOrderForMe.value;
  }

  get imageTemplateId(): number {
    return this.form.controls.imageTemplateId.value;
  }

  get defaultTemplate(): GiftCardImageTemplate {
    return this.templates && this.templates.length > 0 && this.templates[0];
  }

  get selectedTemplate(): GiftCardImageTemplate {
    return this.templates && this.imageTemplateId && this.templates.find(x => x.id === this.imageTemplateId);
  }

  get recipientMessage(): string {
    return this.form.controls.recipientMessage?.value.replace(/(?:\r\n|\r|\n)/g, '<br/>');
  }

  private user: UserProfile;
  private confirmationModal: NgbModalRef;
  private subscriptions: Subscription[] = [];

  constructor(
    private orderService: OrderService,
    private toastService: ToastService,
    private router: Router,
    private ngxUiLoaderService: NgxUiLoaderService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private store: Store,
    public vs: ValidationService,
    private viewportScroller: ViewportScroller
  ) {
  }

  ngOnInit() {
    this.store.dispatch(new StaticDataActions.GetGiftCardImageTemplates());

    this.subscriptions.push(

      this.store.select<any>(state => state.staticData.giftCardImageTemplates)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.templates = response;
          this.checkCartAndToggleImageSlider();
        }),

      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
        })
    );

    this.subscriptions.push(
      this.controls.isOrderForMe.valueChanges.subscribe(x => {
        if (x) {
          this.controls.senderName.disable();
          this.controls.recipientName.disable();
          this.controls.recipientEmail.disable();
          this.controls.recipientMessage.disable();
          this.controls.hasNotificationDate.disable();
        } else {
          this.controls.senderName.enable();
          this.controls.recipientName.enable();
          this.controls.recipientEmail.enable();
          this.controls.recipientMessage.enable();
          this.controls.hasNotificationDate.enable();
        }

        this.form.patchValue({
          senderName: x ? null : this.user.name,
          recipientName: null,
          recipientEmail: null,
          recipientMessage: null,
          hasNotificationDate: false,
          notificationDate: null,
          notificationTime: null,
        });

        this.controls.notificationDate.updateValueAndValidity();
        this.controls.notificationTime.updateValueAndValidity();
      })
    );

    this.subscriptions.push(
      this.controls.hasNotificationDate.valueChanges
        .subscribe(x => {
          if (x) {
            this.controls.notificationDate.enable();
            this.controls.notificationTime.enable();
          } else {
            this.controls.notificationDate.disable();
            this.controls.notificationTime.disable();
          }

          this.form.patchValue({
            notificationDate: null,
            notificationTime: '00:00',
          });

          this.controls.notificationDate.updateValueAndValidity();
          this.controls.notificationTime.updateValueAndValidity();
        })
    );

    // Retrieve previously session form values
    const previousRecipientFormValue = JSON.parse(sessionStorage.getItem(PREVIOUS_RECIPIENT_VALUES_KEY));
    if (previousRecipientFormValue) {
      this.form.patchValue({
        ...previousRecipientFormValue,
        notificationDate: previousRecipientFormValue.notificationDate ? new Date(previousRecipientFormValue.notificationDate) : null,
        notificationTime: previousRecipientFormValue.notificationTime ? previousRecipientFormValue.notificationTime : null,
      });
    }

    this.subscriptions.push(
      this.orderService.getCartItems()
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => {
          })
        )
        .subscribe((cartItems: any[]) => {
          this.cartItems = cartItems;
          this.cartItems.forEach((x) => {
            x.voucherImage = voucherImage(x.voucherImage);
          });
          this.calculateCartCount();
        })
    );

    this.subscriptions.push(
      this.store.select<any>(state => state.staticData.publicSettings)
        .pipe(filter(x => !!x))
        .subscribe((data: any) => {
          this.minimumOrder = data.minOrderAmount;
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    // this.stopImageSlider();
  }

  editOrderItem(id: number, increment: number) {
    const cartItemIndex = this.cartItems.findIndex(x => x.id == id);
    if (cartItemIndex < 0) {
      this.toastService.show('Eroare', 'Item inexistent.', 'error');
      return;
    }

    const item = this.cartItems[cartItemIndex];

    item.quantity = item.quantity + increment;
    item.total = roundNumber(item.quantity * item.amount);

    if (item.quantity < 1) {
      this.deleteOrderItem(id);
      return;
    }

    const model = {
      quantity: item.quantity
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.orderService.updateCartItem(item.id, model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe((response: any) => {
          response.voucherImage = voucherImage(response.voucherImage);
          this.cartItems[cartItemIndex] = response;
          this.calculateCartCount();
        })
    );
  }

  deleteOrderItem(id) {
    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.orderService.deleteCartItem(id)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.cartItems = this.cartItems.filter(x => x.id !== id);
          this.calculateCartCount();
        })
    );
  }

  calculateCartCount() {
    this.totalAmount = roundNumber(this.cartItems.reduce((sum, current) => sum + current.total, 0));
    this.store.dispatch(new AuthActions.SetCartCount(this.cartItems.length));

    this.checkCartAndToggleImageSlider();
  }

  private checkCartAndToggleImageSlider() {
    if (!this.templates || !this.cartItems?.length) {
      return;
    }

    // if (this.hasOnlyGiftShareCards && this.isSliderRunning === false) {
    //   this.isSliderRunning = true;
    //   setTimeout(() => {
    //     this.startImageSlider();
    //   });
    // }
  }

  onSubmit() {
    if (!this.form.valid ||
      !this.hasMinimumOrder ||
      !this.hasRequiredFunds) {
      this.vs.triggerValidation(this.form);
      return;
    }

    this.confirmationModal = this.modalService.open(this.confirmationModalTemplate);

  }

  onCheckoutConfirmSubmit() {
    const order = {
      ...this.form.value,
      notificationDate: this.canScheduleNotification && this.hasNotificationDate ? this.notificationDate : null,
      imageTemplateId: this.hasOnlyGiftShareCards ? this.imageTemplateId : null
    } as Order;

    this.scroll();
    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.orderService.confirmOrder(order)
        .pipe(
          catchError(e => {
            this.error = e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.';
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
            this.confirmationModal.close();
          })
        )
        .subscribe((data: any) => {
          this.order = data;
          const walletAmount = roundNumber(this.user.walletAmount - data.amount);
          this.store.dispatch([
            new AuthActions.SetUserWalletAmount(walletAmount),
            new AuthActions.SetCartCount(0)
          ]);
          sessionStorage.removeItem(PREVIOUS_RECIPIENT_VALUES_KEY);
          this.clearData();
        })
    );

  }

  onDepositClick() {
    this.storeGiftCardData();

    const modalRef = this.modalService.open(DepositModalComponent, { backdrop: 'static', });
    modalRef.componentInstance.title = 'Adauga fonduri';
  }

  getImageTemplateFullThumbnailUrl(template: GiftCardImageTemplate): string {
    const splits = template.url.split('.');
    return template && environment.contentUrl + '/giftcard-image-templates/' + splits[0] + '_thumbnail.' + splits[1];
  }

  getImageTemplateFullUrl(template: GiftCardImageTemplate): string {
    return template && environment.contentUrl + '/giftcard-image-templates/' + template.url;
  }

  onSelectTemplateClick(template: GiftCardImageTemplate) {
    this.form.patchValue({
      imageTemplateId: template.id
    });
  }

  isTemplateSelected(template: GiftCardImageTemplate): boolean {
    return this.selectedTemplate && template.name === this.selectedTemplate.name;
  }

  /**
   * Store form values in sessionStorage to repopulate if user triggers other actions like Add funds, or Continue shopping
   *
   * @private
   * @memberof CartComponent
   */
  private storeGiftCardData() {
    if (this.form.dirty) {
      sessionStorage.setItem(PREVIOUS_RECIPIENT_VALUES_KEY, JSON.stringify({
        ...this.form.value
      }));
    }
  }

  private clearData() {
    this.form.reset({
      isOrderForMe: true,
      senderName: null,
      recipientName: null,
      recipientEmail: null,
      recipientMessage: null,
      hasNotificationDate: false,
      notificationDate: null,
      imageTemplateId: 1
    });
  }

  private scroll() {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

}
